"use client"

import * as React from "react"
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react"
import { DropdownMenu as Menu } from "radix-ui"

import { cn } from "@ui/lib/utils"
import { isOverlayKeepOpenTarget } from "@ui/components/ui/overlay-keep-open"

type SelectContextValue = {
  value: string
  disabled: boolean
  required: boolean
  open: boolean
  labels: Map<string, React.ReactNode>
  triggerRef: React.RefObject<HTMLButtonElement | null>
  setTriggerNode: (node: HTMLButtonElement | null) => void
  onValueChange: (value: string) => void
  registerLabels: (labels: Map<string, React.ReactNode>) => void
}

const SelectContext = React.createContext<SelectContextValue | null>(null)

function useSelectContext() {
  const context = React.useContext(SelectContext)
  if (!context) throw new Error("Select components must be used inside Select")
  return context
}

type SelectProps = {
  children: React.ReactNode
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  required?: boolean
  name?: string
  autoComplete?: string
  form?: string
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  dir?: "ltr" | "rtl"
}

function Select({
  children,
  value: controlledValue,
  defaultValue,
  onValueChange,
  disabled = false,
  required = false,
  name,
  autoComplete,
  form,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  dir,
}: SelectProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? "")
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen)
  const [labels, setLabels] = React.useState<Map<string, React.ReactNode>>(new Map())
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const setTriggerNode = React.useCallback((node: HTMLButtonElement | null) => {
    triggerRef.current = node
  }, [])
  const value = controlledValue ?? internalValue
  const open = controlledOpen ?? internalOpen

  const setValue = React.useCallback((nextValue: string) => {
    if (controlledValue === undefined) setInternalValue(nextValue)
    onValueChange?.(nextValue)
  }, [controlledValue, onValueChange])

  const setOpen = React.useCallback((nextOpen: boolean) => {
    if (disabled && nextOpen) return
    if (controlledOpen === undefined) setInternalOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }, [controlledOpen, disabled, onOpenChange])

  const context = React.useMemo<SelectContextValue>(() => ({
    value,
    disabled,
    required,
    open,
    labels,
    triggerRef,
    setTriggerNode,
    onValueChange: setValue,
    registerLabels: setLabels,
  }), [value, disabled, required, open, labels, setTriggerNode, setValue])

  return (
    <SelectContext.Provider value={context}>
      <Menu.Root open={open} onOpenChange={setOpen} dir={dir} modal={false}>
        {children}
      </Menu.Root>
      <select
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
        name={name}
        autoComplete={autoComplete}
        form={form}
        value={value}
        required={required}
        disabled={disabled}
        onChange={(event) => setValue(event.target.value)}
        onInvalid={(event) => {
          event.preventDefault()
          triggerRef.current?.focus()
        }}
      >
        <option value="" />
        {Array.from(labels, ([itemValue, label]) => (
          <option key={itemValue} value={itemValue}>{plainText(label)}</option>
        ))}
      </select>
    </SelectContext.Provider>
  )
}

function plainText(value: React.ReactNode): string {
  if (typeof value === "string" || typeof value === "number") return String(value)
  if (Array.isArray(value)) return value.map(plainText).join("")
  if (React.isValidElement<{ children?: React.ReactNode }>(value))
    return plainText(value.props.children)
  return ""
}

function collectLabels(
  children: React.ReactNode,
  labels: Map<string, React.ReactNode>,
): void {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement<{ value?: string; children?: React.ReactNode }>(child)) return
    if (child.type === SelectItem && typeof child.props.value === "string") {
      labels.set(child.props.value, child.props.children)
    } else {
      collectLabels(child.props.children, labels)
    }
  })
}

function SelectGroup({ ...props }: React.ComponentProps<typeof Menu.Group>) {
  return <Menu.Group data-slot="select-group" {...props} />
}

function SelectValue({
  placeholder,
  children,
  ...props
}: React.ComponentProps<"span"> & { placeholder?: React.ReactNode }) {
  const { value, labels } = useSelectContext()
  return (
    <span data-slot="select-value" {...props}>
      {children ?? (value ? labels.get(value) : undefined) ?? placeholder}
    </span>
  )
}

const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Menu.Trigger> & { size?: "sm" | "default" }
>(function SelectTrigger({ className, size = "default", children, disabled, ...props }, ref) {
  const context = useSelectContext()
  return (
    <Menu.Trigger
      ref={(node) => {
        context.setTriggerNode(node)
        if (typeof ref === "function") ref(node)
        else if (ref) ref.current = node
      }}
      data-slot="select-trigger"
      data-size={size}
      data-placeholder={context.value ? undefined : ""}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={context.open}
      aria-required={context.required || undefined}
      disabled={context.disabled || disabled}
      className={cn(
        "flex w-fit items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[placeholder]:text-muted-foreground data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon className="size-4 opacity-50" aria-hidden="true" />
    </Menu.Trigger>
  )
})

type SelectContentProps = React.ComponentProps<typeof Menu.Content> & {
  position?: "item-aligned" | "popper"
}

function SelectContent({
  className,
  children,
  position = "item-aligned",
  align = "start",
  onInteractOutside,
  ...props
}: SelectContentProps) {
  const { value, onValueChange, registerLabels } = useSelectContext()
  const labels = React.useMemo(() => {
    const collected = new Map<string, React.ReactNode>()
    collectLabels(children, collected)
    return collected
  }, [children])

  React.useEffect(() => registerLabels(labels), [labels, registerLabels])

  return (
    <Menu.Portal>
      <Menu.Content
        data-slot="select-content"
        role="listbox"
        align={align}
        sideOffset={position === "popper" ? 4 : 0}
        onInteractOutside={(event) => {
          onInteractOutside?.(event)
          if (isOverlayKeepOpenTarget(event.target)) event.preventDefault()
        }}
        className={cn(
          "relative z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[max(8rem,var(--radix-dropdown-menu-trigger-width))] overflow-x-hidden overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
          className,
        )}
        {...props}
      >
        <Menu.RadioGroup value={value} onValueChange={onValueChange}>
          {children}
        </Menu.RadioGroup>
      </Menu.Content>
    </Menu.Portal>
  )
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof Menu.Label>) {
  return <Menu.Label data-slot="select-label" className={cn("px-2 py-1.5 text-xs font-medium", className)} {...props} />
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof Menu.RadioItem>) {
  const { value } = useSelectContext()
  return (
    <Menu.RadioItem
      data-slot="select-item"
      role="option"
      aria-selected={value === props.value}
      className={cn(
        "relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center" aria-hidden="true">
        <Menu.ItemIndicator><CheckIcon className="size-4" /></Menu.ItemIndicator>
      </span>
      {children}
    </Menu.RadioItem>
  )
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof Menu.Separator>) {
  return <Menu.Separator data-slot="select-separator" className={cn("-mx-1 my-1 h-px bg-border", className)} {...props} />
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="select-scroll-up-button" className={cn("flex items-center justify-center py-1", className)} {...props}><ChevronUpIcon className="size-4" /></div>
}

function SelectScrollDownButton({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="select-scroll-down-button" className={cn("flex items-center justify-center py-1", className)} {...props}><ChevronDownIcon className="size-4" /></div>
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
